import React, { useEffect, useMemo, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Phone, Filter, Search } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { useBalk } from "@/lib/balkState";
import { ErrorState, EmptyState } from "@/components/ListState";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { PriorityBadge, FaultStatusBadge, formatNlDate } from "@/components/Badges";
import { Code } from "@/components/Code";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MediaUpload } from "@/components/MediaUpload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_OPTS = [
  ["open", "Open"], ["in_behandeling", "In behandeling"], ["wacht_op_onderdelen", "Wacht op onderdelen"],
  ["gemeld_bij_verhuurder", "Gemeld bij verhuurder"], ["opgelost", "Opgelost"], ["afgewezen", "Afgewezen"],
];
const PRIO_OPTS = [["P1", "P1 kritiek"], ["P2", "P2 hoog"], ["P3", "P3 normaal"], ["P4", "P4 laag"]];
const CLOSED_STATUSES = ["opgelost", "afgewezen"];
const PRIO_RANK = { P1: 0, P2: 1, P3: 2, P4: 3 };

const emptyForm = {
  location_id: "", ruimte_id: "__none__", asset_id: "__none__", description: "",
  priority: "P3", reporter_name: "", reporter_source: "telefoon", media: [],
};

export default function StoringenList() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const { refresh: refreshBalk } = useBalk();
  const [searchParams, setSearchParams] = useSearchParams();
  const [faults, setFaults] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ruimtes, setRuimtes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Filters/search persist in the URL so the back button restores them and a
  // filtered view can be shared. Default scope is open faults only.
  const [scope, setScope] = useState(searchParams.get("scope") || "open");
  const [filters, setFilters] = useState({
    priority: searchParams.get("priority") || "all",
    location_id: searchParams.get("location_id") || "all",
    assigned_to: searchParams.get("assigned_to") || "all",
  });
  const [search, setSearch] = useState(searchParams.get("q") || "");
  // Arriving from a scanned sticker: /storingen?new=1&loc=<id>&asset=<id>
  const scanPrefill = useMemo(() => {
    if (searchParams.get("new") !== "1") return null;
    return {
      ...emptyForm,
      location_id: searchParams.get("loc") || "",
      asset_id: searchParams.get("asset") || "__none__",
      reporter_source: "app",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [open, setOpen] = useState(Boolean(scanPrefill));
  const [form, setForm] = useState(scanPrefill || emptyForm);
  // What "unchanged" means right now: the scan prefill on arrival, an empty form after that.
  const [baseline, setBaseline] = useState(scanPrefill || emptyForm);
  const [saving, setSaving] = useState(false);

  // Keep the URL query string in sync (skip default/empty values).
  useEffect(() => {
    const p = {};
    if (scope !== "open") p.scope = scope;
    if (filters.priority !== "all") p.priority = filters.priority;
    if (filters.location_id !== "all") p.location_id = filters.location_id;
    if (filters.assigned_to !== "all") p.assigned_to = filters.assigned_to;
    if (search.trim()) p.q = search.trim();
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, filters, search]);
  const faultGuard = useUnsavedGuard(JSON.stringify(form) !== JSON.stringify(baseline), () => setOpen(false));

  const loadRefs = async () => {
    const [loc, rui, ass, asg] = await Promise.all([
      api.get("/locations"), api.get("/ruimtes"), api.get("/assets"), api.get("/users/assignees"),
    ]);
    setLocations(loc.data); setRuimtes(rui.data); setAssets(ass.data); setAssignees(asg.data);
  };

  const loadFaults = async () => {
    setError(""); // clear any prior error so a successful reload recovers the list
    const params = new URLSearchParams();
    if (scope === "open") params.set("open_only", "true");
    if (filters.priority !== "all") params.set("priority", filters.priority);
    if (filters.location_id !== "all") params.set("location_id", filters.location_id);
    if (filters.assigned_to !== "all") params.set("assigned_to", filters.assigned_to);
    const { data } = await api.get(`/faults?${params.toString()}`);
    setFaults(data);
  };

  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try { await loadRefs(); await loadFaults(); }
      catch (e) { setError(getErrorMessage(e, "Kan storingen niet laden.")); }
      finally { setLoading(false); }
    })();
  }, []);

  const reload = async () => {
    setLoading(true); setError("");
    try { await loadRefs(); await loadFaults(); }
    catch (e) { setError(getErrorMessage(e, "Kan storingen niet laden.")); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!loading) loadFaults().catch((e) => setError(getErrorMessage(e, "Kan storingen niet laden."))); /* eslint-disable-next-line */ }, [scope, filters]);

  // Search is client-side (number, description, location); closed faults always
  // sort to the bottom regardless of priority, so a resolved P1 never outranks
  // an open P3.
  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = faults;
    if (q) {
      rows = rows.filter((f) => [f.fault_number, f.description, f.location_name]
        .filter(Boolean).join(" ").toLowerCase().includes(q));
    }
    return rows.slice().sort((a, b) => {
      const ac = CLOSED_STATUSES.includes(a.status) ? 1 : 0;
      const bc = CLOSED_STATUSES.includes(b.status) ? 1 : 0;
      if (ac !== bc) return ac - bc;
      const ap = PRIO_RANK[a.priority] ?? 9;
      const bp = PRIO_RANK[b.priority] ?? 9;
      if (ap !== bp) return ap - bp;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [faults, search]);

  const formRuimtes = useMemo(
    () => ruimtes.filter((r) => r.location_id === form.location_id),
    [ruimtes, form.location_id]
  );
  // Pick the place first, then only the machines that stand there: at a site
  // with 20 machines that is the difference between a list and a scroll.
  const formAssets = useMemo(
    () => assets.filter((a) => a.location_id === form.location_id
      && (form.ruimte_id === "__none__" || a.ruimte_id === form.ruimte_id)),
    [assets, form.location_id, form.ruimte_id]
  );

  const submit = async () => {
    if (!form.location_id) { toast.error("Kies een locatie."); return; }
    setSaving(true);
    try {
      const chosen = assets.find((a) => a.id === form.asset_id);
      await api.post("/faults", {
        location_id: form.location_id,
        ruimte_id: form.ruimte_id === "__none__" ? (chosen?.ruimte_id || null) : form.ruimte_id,
        // The spoor comes with the machine; it is corrected on the storing
        // itself when the report turns out to be about another bay (rule 42).
        spoor: chosen?.spoor || null,
        asset_id: form.asset_id === "__none__" ? null : form.asset_id,
        description: form.description,
        priority: form.priority,
        reporter_name: form.reporter_name,
        reporter_source: form.reporter_source,
        media: form.media,
      });
      toast.success("Storing gelogd");
      refreshBalk();
      setOpen(false); setForm(emptyForm); setBaseline(emptyForm);
      await loadFaults();
    } catch (e) {
      toast.error(getErrorMessage(e, "Loggen mislukt."));
    } finally { setSaving(false); }
  };

  const consoleFacts = loading
    ? ["laden…"]
    : [`${displayed.length} getoond`,
       ...(scope === "open" ? ["openstaand"] : []),
       `${faults.filter((f) => f.priority === "P1" && !["opgelost", "afgewezen"].includes(f.status)).length} P1 open`];
  const consoleActions = canWrite()
    ? [{ key: "loggen", label: "Storing loggen", icon: Phone, variant: "primary",
         testid: "fault-log-button", onClick: () => { setForm(emptyForm); setBaseline(emptyForm); setOpen(true); } }]
    : [];

  return (
    <Page console={{ variant: "view", title: "Storingen", facts: consoleFacts, actions: consoleActions }}>
      <div className="space-y-4">

      {/* Filters + search */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search size={18} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="faults-search-input" className="h-9 pl-8" placeholder="Zoek op nummer, omschrijving of locatie" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="h-9 w-auto min-w-[190px]" data-testid="filter-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Openstaand (standaard)</SelectItem>
              <SelectItem value="all">Alles incl. afgehandeld</SelectItem>
            </SelectContent>
          </Select>
          <span className="flex items-center gap-1 text-data text-muted-foreground"><Filter size={14} /></span>
          <FilterSelect testid="filter-priority" value={filters.priority} onChange={(v) => setFilters((f) => ({ ...f, priority: v }))} placeholder="Prioriteit" options={PRIO_OPTS} />
          <FilterSelect testid="filter-location" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v }))} placeholder="Locatie" options={locations.map((l) => [l.location_id, l.name])} />
          <FilterSelect testid="filter-assignee" value={filters.assigned_to} onChange={(v) => setFilters((f) => ({ ...f, assigned_to: v }))} placeholder="Toegewezen" options={assignees.map((u) => [u.id, u.name])} />
        </div>
      </Card>

      {error && !loading ? (
        <ErrorState testid="faults-load-error" message={error} onRetry={reload} />
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Card key={i} className="p-4"><Skeleton className="h-14 w-full" /></Card>)}
        </div>
      ) : displayed.length === 0 ? (
        <EmptyState
          testid="faults-empty"
          title={search.trim() ? "Geen storing past bij deze zoekopdracht" : (scope === "open" ? "Niets open" : "Nog geen storingen gelogd")}
          purpose={search.trim()
            ? "Hier staat elke gemelde storing, op prioriteit gesorteerd. Wis je zoekopdracht om alles weer te zien."
            : (scope === "open"
              ? "Alle gemelde storingen zijn afgehandeld. Zodra iemand belt of een sticker scant, komt de melding hier binnen."
              : "Hier komt elke storing binnen: van de telefoon, van een gescande sticker of van jezelf. Log de eerste zodra er iets stilstaat.")}
          action={canWrite()
            ? { label: "Storing loggen", icon: Phone, onClick: () => { setForm(emptyForm); setBaseline(emptyForm); setOpen(true); } }
            : null}
        />
      ) : (
        <>
          {/* Desktop: a real table. Mobile keeps the cards. */}
          <Card className="hidden sm:block">
            <div>
              <Table data-testid="faults-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">Nummer</TableHead>
                    <TableHead className="w-[130px]">Prioriteit</TableHead>
                    <TableHead className="w-[160px]">Locatie</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead className="w-[130px]">Asset</TableHead>
                    <TableHead className="w-[140px]">Toegewezen</TableHead>
                    <TableHead className="w-[110px]">Gemeld</TableHead>
                    <TableHead className="w-[170px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayed.map((f) => (
                    <TableRow
                      key={f.id}
                      data-testid={`fault-row-${f.id}`}
                      className="cursor-pointer"
                      onClick={() => navigate(`/storingen/${f.id}`)}
                    >
                      <TableCell><Code>{f.fault_number}</Code></TableCell>
                      <TableCell><PriorityBadge priority={f.priority} /></TableCell>
                      <TableCell className="text-body">{[f.location_name, f.ruimte_name, f.spoor].filter(Boolean).join(" · ")}</TableCell>
                      <TableCell className="text-body">{f.description || <span className="text-muted-foreground">(geen omschrijving)</span>}</TableCell>
                      <TableCell>{f.asset_code ? <Code>{f.asset_code}</Code> : null}</TableCell>
                      <TableCell className="text-data text-muted-foreground">{f.assignee_name || "Niet toegewezen"}</TableCell>
                      <TableCell><Code size="small">{formatNlDate((f.created_at || "").slice(0, 10))}</Code></TableCell>
                      <TableCell><FaultStatusBadge status={f.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="space-y-2 sm:hidden" data-testid="faults-cardlist">
            {displayed.map((f) => (
              <Card
                key={f.id}
                data-testid={`fault-card-${f.id}`}
                className="cursor-pointer p-4 transition-colors hover:bg-accent"
                onClick={() => navigate(`/storingen/${f.id}`)}
              >
                <div className="flex items-start justify-between gap-2">
                  <Code>{f.fault_number}</Code>
                  <Code size="small">{formatNlDate((f.created_at || "").slice(0, 10))}</Code>
                </div>
                <div className="mt-1 text-body text-foreground">{f.description || "(geen omschrijving)"}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-data text-muted-foreground">
                  <span>{[f.location_name, f.ruimte_name, f.spoor].filter(Boolean).join(" — ")}</span>
                  {f.asset_code && <span>Asset: {f.asset_code}</span>}
                  <span>{f.assignee_name ? `Toegewezen: ${f.assignee_name}` : "Niet toegewezen"}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <PriorityBadge priority={f.priority} />
                  <FaultStatusBadge status={f.status} />
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Fast phone/app intake dialog */}
      <Dialog open={open} onOpenChange={faultGuard.requestClose}>
        <DialogContent className="max-w-lg" srDescription="Leg een nieuwe storing vast: locatie, asset, prioriteit en omschrijving.">
          <DiscardDialog guard={faultGuard} />
          <DialogHeader><DialogTitle>Storing loggen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Locatie *</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v, ruimte_id: "__none__", asset_id: "__none__" }))}>
                <SelectTrigger data-testid="fault-location-select"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Waar staat het?</Label>
                <Select value={form.ruimte_id} onValueChange={(v) => setForm((f) => ({ ...f, ruimte_id: v, asset_id: "__none__" }))} disabled={!form.location_id}>
                  <SelectTrigger data-testid="fault-ruimte-select"><SelectValue placeholder="Weet ik niet" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Weet ik niet</SelectItem>{formRuimtes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Prioriteit</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="fault-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIO_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Asset (optioneel)</Label>
              <Select value={form.asset_id} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v }))} disabled={!form.location_id}>
                <SelectTrigger><SelectValue placeholder="Geen" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Geen</SelectItem>{formAssets.map((a) => <SelectItem key={a.id} value={a.id}>{[a.name || `${a.brand} ${a.model}`.trim(), a.spoor].filter(Boolean).join(" · ") || a.asset_code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Omschrijving</Label><Textarea data-testid="fault-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Melder (naam)</Label><Input value={form.reporter_name} onChange={(e) => setForm((f) => ({ ...f, reporter_name: e.target.value }))} placeholder="Bijv. chauffeur" /></div>
              <div className="space-y-1.5">
                <Label>Bron</Label>
                <Select value={form.reporter_source} onValueChange={(v) => setForm((f) => ({ ...f, reporter_source: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="telefoon">Telefoon</SelectItem><SelectItem value="app">App</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Media</Label>
              <MediaUpload value={form.media} onChange={(m) => setForm((f) => ({ ...f, media: m }))} apiClient={api} uploadPath="/storage/upload" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => faultGuard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="fault-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Loggen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Page>
  );
}

const FilterSelect = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-auto min-w-[130px]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}: alle</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);
